// Roof pitch readout shared by both roof-estimate surfaces (the SAM Quick Quote card and the
// property detail's Roof Estimate section): the predominant pitch plus its per-pitch area
// breakdown, the same summary a professional aerial report gives.
//
// The pitch comes from Google Solar's per-facet 3D data — a flat top-down satellite image alone
// cannot yield pitch, since pitch needs a vertical rise an overhead view doesn't contain. It is
// applied automatically to traced areas (footprint → real sloped area); there's deliberately no
// manual pitch picker, since the measured value is better than anything a user would guess.
import { useState } from 'react';
import { pitchMultiplier } from '@/lib/roofPitch';

export interface PitchBreakdownRow {
  pitch: string;
  rise_per_12: number;
  area_sqft: number;
  squares: number;
  share_pct: number;
}

interface RoofPitchPanelProps {
  /** Area-weighted predominant pitch, e.g. "8/12" — null when unavailable for this address. */
  predominantPitch?: string | null;
  predominantPitchDegrees?: number | null;
  pitchBreakdown?: PitchBreakdownRow[];
  segmentCount?: number;
  /** Flat footprint area from the Solar API, for reference against a traced footprint. */
  groundAreaSqft?: number | null;
}

const fmt = (n: number | null | undefined, digits = 0): string =>
  n == null ? '' : n.toLocaleString(undefined, { maximumFractionDigits: digits });

export function RoofPitchPanel({
  predominantPitch,
  predominantPitchDegrees,
  pitchBreakdown = [],
  segmentCount,
  groundAreaSqft,
}: RoofPitchPanelProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  if (!predominantPitch) {
    return (
      <div className="rounded-xl border border-ui-border bg-ui-subtle px-3 py-2">
        <p className="mb-1 text-micro font-semibold uppercase tracking-wide text-ui-text3">Pitch</p>
        <p className="text-xs italic text-ui-text3">
          No pitch data for this address — traced areas are reported as flat footprint.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-ui-border bg-ui-subtle px-3 py-2 space-y-1.5">
      <p className="text-micro font-semibold uppercase tracking-wide text-ui-text3">Pitch</p>

      <p className="text-sm text-ui-text">
        <span className="font-semibold">{predominantPitch}</span>
        {predominantPitchDegrees != null && (
          <span className="text-ui-text2"> · {predominantPitchDegrees.toFixed(1)}°</span>
        )}
        <span className="text-ui-text3">
          {' '}· ×{pitchMultiplier(parseFloat(predominantPitch)).toFixed(3)} area factor
        </span>
        {segmentCount ? <span className="text-ui-text3"> · {segmentCount} facets</span> : null}
      </p>

      {groundAreaSqft != null && (
        <p className="text-xs text-ui-text3">
          Footprint {fmt(groundAreaSqft)} sqft (flat) — what tracing the image measures.
        </p>
      )}

      {/* Per-pitch area breakdown — lets a quote separate steep-charge facets from the main field. */}
      {pitchBreakdown.length > 1 && (
        <div>
          <button
            type="button"
            onClick={() => setShowBreakdown((v) => !v)}
            className="text-xs font-medium text-accent-text hover:opacity-80"
          >
            {showBreakdown ? 'Hide' : 'Show'} pitch breakdown ({pitchBreakdown.length})
          </button>
          {showBreakdown && (
            <div className="mt-1 space-y-0.5">
              {pitchBreakdown.map((row) => (
                <div key={row.pitch} className="flex items-center justify-between text-xs">
                  <span className="font-medium text-ui-text">{row.pitch}</span>
                  <span className="text-ui-text2">
                    {fmt(row.area_sqft)} sqft · {fmt(row.squares, 1)} sq · {row.share_pct}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RoofPitchPanel;
